import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { DashboardCard, EmptyState } from "../../components/kit";
import { toast } from "sonner";
import {
  Check, ChevronLeft, ChevronRight, CalendarRange, MapPin, Gavel,
  Wallet, Rocket, Trash2, Plus,
} from "lucide-react";

const STEPS = [
  { id: "basics", label: "Basics", icon: CalendarRange },
  { id: "venue", label: "Venue", icon: MapPin },
  { id: "committees", label: "Committees", icon: Gavel },
  { id: "pricing", label: "Pricing", icon: Wallet },
  { id: "review", label: "Launch", icon: Rocket },
];

const slugify = (s) =>
  (s || "").toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function EventCreationWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [orgs, setOrgs] = useState([]);

  const [form, setForm] = useState({
    organization_id: "",
    title: "",
    slug: "",
    tagline: "",
    description: "",
    banner_url: "https://images.unsplash.com/photo-1725618878496-233974f2fd59",
    start_date: "",
    end_date: "",
    location: "",
    format: "hybrid",
    max_delegates: 300,
    currency: "INR",
    status: "draft",
  });
  const [committees, setCommittees] = useState([
    { name: "", topic: "", type: "GA", level: "Intermediate", capacity: 40 },
  ]);
  const [tiers, setTiers] = useState([
    { tier_name: "Early Bird", price: 1999, includes: "Delegate kit, Lunch, Certificate" },
    { tier_name: "Regular", price: 2499, includes: "Delegate kit, Lunch, Certificate" },
  ]);

  useEffect(() => {
    api.get("/v2/organisations").then((r) => {
      const list = r.data.items || [];
      setOrgs(list);
      if (!form.organization_id) {
        const initial = user?.organization_id || list[0]?.id || "";
        setForm((f) => ({ ...f, organization_id: initial }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Step 1 validation
  const canNext = () => {
    if (step === 0) return form.title && form.slug && form.start_date && form.end_date && form.organization_id;
    if (step === 1) return form.location && form.format;
    if (step === 2) return committees.length > 0 && committees.every((c) => c.name && c.topic);
    if (step === 3) return tiers.every((t) => t.tier_name && t.price >= 0);
    return true;
  };

  const launch = async (publish = false) => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
        status: publish ? "published" : "draft",
        price: tiers[0]?.price || 0,
      };
      const { data: created } = await api.post("/v2/events", payload);
      const eventId = created.id;

      // committees
      for (const c of committees) {
        await api.post("/v2/committees", {
          event_id: eventId,
          name: c.name,
          topic: c.topic,
          type: c.type,
          level: c.level,
          capacity: Number(c.capacity) || 40,
          description: c.description || "",
        });
      }
      // pricing tiers
      for (const t of tiers) {
        await api.post(`/v2/events/${eventId}/pricing`, {
          event_id: eventId,
          tier_name: t.tier_name,
          price: Number(t.price) || 0,
          currency: form.currency,
          includes: (t.includes || "").split(",").map((s) => s.trim()).filter(Boolean),
        });
      }
      if (publish) {
        await api.post(`/v2/events/${eventId}/publish`);
      }
      toast.success(publish ? "Event published" : "Event saved as draft");
      nav(`/dashboard/events/${eventId}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSubmitting(false);
  };

  const addCommittee = () => setCommittees((arr) => [...arr, { name: "", topic: "", type: "GA", level: "Intermediate", capacity: 40 }]);
  const removeCommittee = (i) => setCommittees((arr) => arr.filter((_, idx) => idx !== i));
  const patchCommittee = (i, patch) => setCommittees((arr) => arr.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const addTier = () => setTiers((arr) => [...arr, { tier_name: "", price: 0, includes: "" }]);
  const removeTier = (i) => setTiers((arr) => arr.filter((_, idx) => idx !== i));
  const patchTier = (i, patch) => setTiers((arr) => arr.map((t, idx) => idx === i ? { ...t, ...patch } : t));

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            New event
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Create a conference in 5 steps.
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Every detail here will power your event microsite, registrations, allocations, and live ops.
          </p>
        </div>

        {/* Stepper */}
        <div className="mt-10 rounded-xl border border-slate-200 bg-white p-4 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === i;
              const isDone = step > i;
              return (
                <div key={s.id} className="flex items-center gap-2" data-testid={`wizard-step-${s.id}`}>
                  <button
                    onClick={() => setStep(i)}
                    disabled={step < i && !canNext()}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.18em] font-bold transition-all
                      ${isActive
                        ? "bg-[#0A192F] text-white"
                        : isDone
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                  >
                    {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                    {s.label}
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8"
        >
          {step === 0 && (
            <DashboardCard eyebrow="Step 1" title="Event basics" description="Name, branding, dates.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {user?.role === "super_admin" && (
                  <div className="md:col-span-2">
                    <Label>Organisation</Label>
                    <Select value={form.organization_id} onValueChange={(v) => patch({ organization_id: v })}>
                      <SelectTrigger data-testid="wizard-org-select"><SelectValue placeholder="Choose organisation" /></SelectTrigger>
                      <SelectContent>
                        {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Title</Label>
                  <Input
                    required
                    value={form.title}
                    onChange={(e) => patch({ title: e.target.value, slug: form.slug || slugify(e.target.value) })}
                    placeholder="Globex MUN 2026"
                    data-testid="wizard-title-input"
                  />
                </div>
                <div>
                  <Label>Slug (URL handle)</Label>
                  <Input required value={form.slug} onChange={(e) => patch({ slug: slugify(e.target.value) })}
                    placeholder="globex-mun-2026" data-testid="wizard-slug-input" />
                </div>
                <div className="md:col-span-2">
                  <Label>Tagline</Label>
                  <Input value={form.tagline} onChange={(e) => patch({ tagline: e.target.value })}
                    placeholder="Debate. Decide. Deliver." />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Textarea rows={4} value={form.description} onChange={(e) => patch({ description: e.target.value })}
                    placeholder="What makes this conference different…" />
                </div>
                <div>
                  <Label>Start date</Label>
                  <Input type="date" required value={form.start_date} onChange={(e) => patch({ start_date: e.target.value })} data-testid="wizard-start-date" />
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" required value={form.end_date} onChange={(e) => patch({ end_date: e.target.value })} data-testid="wizard-end-date" />
                </div>
                <div className="md:col-span-2">
                  <Label>Banner image URL</Label>
                  <Input value={form.banner_url} onChange={(e) => patch({ banner_url: e.target.value })} />
                  {form.banner_url && (
                    <div className="mt-3 h-24 rounded-md border border-slate-200 overflow-hidden bg-cover bg-center"
                         style={{ backgroundImage: `url(${form.banner_url})` }} />
                  )}
                </div>
              </div>
            </DashboardCard>
          )}

          {step === 1 && (
            <DashboardCard eyebrow="Step 2" title="Venue & format" description="Where and how it will run.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Location</Label>
                  <Input required value={form.location} onChange={(e) => patch({ location: e.target.value })}
                    placeholder="Taj Lands End, Mumbai" data-testid="wizard-location-input" />
                </div>
                <div>
                  <Label>Format</Label>
                  <Select value={form.format} onValueChange={(v) => patch({ format: v })}>
                    <SelectTrigger data-testid="wizard-format-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="offline">In-person</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max delegates</Label>
                  <Input type="number" min={1} value={form.max_delegates}
                    onChange={(e) => patch({ max_delegates: Number(e.target.value) })} />
                </div>
              </div>
            </DashboardCard>
          )}

          {step === 2 && (
            <DashboardCard
              eyebrow="Step 3"
              title="Committees"
              description="Define the councils delegates can register for."
              action={(
                <Button onClick={addCommittee} variant="outline" className="border-[#1E3A8A] text-[#1E3A8A]" data-testid="add-committee-button">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              )}
            >
              <div className="space-y-4">
                {committees.map((c, i) => (
                  <div key={i} className="p-5 rounded-lg border border-slate-200 bg-slate-50 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid={`committee-row-${i}`}>
                    <div className="md:col-span-4">
                      <Label>Name</Label>
                      <Input value={c.name} onChange={(e) => patchCommittee(i, { name: e.target.value })}
                        placeholder="UN Security Council" />
                    </div>
                    <div className="md:col-span-4">
                      <Label>Topic / Agenda</Label>
                      <Input value={c.topic} onChange={(e) => patchCommittee(i, { topic: e.target.value })}
                        placeholder="Global AI Governance" />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Type</Label>
                      <Select value={c.type} onValueChange={(v) => patchCommittee(i, { type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GA">GA</SelectItem>
                          <SelectItem value="Specialized">Specialized</SelectItem>
                          <SelectItem value="Crisis">Crisis</SelectItem>
                          <SelectItem value="Press">Press</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-1">
                      <Label>Seats</Label>
                      <Input type="number" min={1} value={c.capacity} onChange={(e) => patchCommittee(i, { capacity: e.target.value })} />
                    </div>
                    <div className="md:col-span-1 flex items-end justify-end">
                      {committees.length > 1 && (
                        <Button variant="outline" onClick={() => removeCommittee(i)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`remove-committee-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {committees.length === 0 && (
                  <EmptyState
                    title="No committees yet"
                    description="Every MUN needs a committee. Add at least one to continue."
                  />
                )}
              </div>
            </DashboardCard>
          )}

          {step === 3 && (
            <DashboardCard
              eyebrow="Step 4"
              title="Pricing tiers"
              description="Stack early-bird, regular and late fees."
              action={(
                <Button onClick={addTier} variant="outline" className="border-[#1E3A8A] text-[#1E3A8A]" data-testid="add-tier-button">
                  <Plus className="h-4 w-4 mr-1" /> Add tier
                </Button>
              )}
            >
              <div className="space-y-4">
                {tiers.map((t, i) => (
                  <div key={i} className="p-5 rounded-lg border border-slate-200 bg-slate-50 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid={`tier-row-${i}`}>
                    <div className="md:col-span-3">
                      <Label>Tier name</Label>
                      <Input value={t.tier_name} onChange={(e) => patchTier(i, { tier_name: e.target.value })} placeholder="Early Bird" />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Price (₹)</Label>
                      <Input type="number" min={0} value={t.price} onChange={(e) => patchTier(i, { price: e.target.value })} />
                    </div>
                    <div className="md:col-span-6">
                      <Label>Includes (comma separated)</Label>
                      <Input value={t.includes} onChange={(e) => patchTier(i, { includes: e.target.value })} placeholder="Delegate kit, Lunch, Certificate" />
                    </div>
                    <div className="md:col-span-1 flex items-end justify-end">
                      {tiers.length > 1 && (
                        <Button variant="outline" onClick={() => removeTier(i)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`remove-tier-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </DashboardCard>
          )}

          {step === 4 && (
            <DashboardCard eyebrow="Step 5" title="Launch" description="One last look.">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Stat label="Title" value={form.title || "—"} />
                <Stat label="Dates" value={`${form.start_date || "—"} → ${form.end_date || "—"}`} />
                <Stat label="Format" value={form.format?.toUpperCase()} />
                <Stat label="Location" value={form.location || "—"} />
                <Stat label="Committees" value={committees.length} />
                <Stat label="Starting tier" value={`₹${tiers[0]?.price || 0}`} />
              </div>
              <p className="mt-6 text-xs text-slate-500">
                You can launch now (public) or save as draft and publish later from the event detail page.
              </p>
            </DashboardCard>
          )}
        </motion.div>

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="wizard-back-button"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => canNext() && setStep((s) => s + 1)}
              disabled={!canNext()}
              className="bg-[#1E3A8A] hover:bg-[#172b6a] text-white"
              data-testid="wizard-next-button"
            >
              Continue <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => launch(false)}
                disabled={submitting}
                data-testid="wizard-save-draft-button"
              >
                Save draft
              </Button>
              <Button
                onClick={() => launch(true)}
                disabled={submitting}
                className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold"
                data-testid="wizard-publish-button"
              >
                <Rocket className="h-4 w-4 mr-1" /> {submitting ? "Launching…" : "Publish"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-4 rounded-md bg-slate-50 border border-slate-200">
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
      <p className="mt-1 font-semibold text-[#0A192F]">{value}</p>
    </div>
  );
}
